import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { queryKeys } from '@/api/queryKeys'
import type { components } from '@/../types/api'

export type SafetyRescanOut = components['schemas']['SafetyRescanOut']
export type SafetyVerdictOut = components['schemas']['SafetyVerdictOut']
export type SafetyAuditEntryOut = components['schemas']['SafetyAuditEntryOut']
export type SafetyReviewOut = components['schemas']['SafetyReviewOut']
export type SafetyReviewDecision = 'approve' | 'reject'

export interface SafetyDetailParams {
  videoId: string
  lang: string
  /** When false, the query is disabled — used to lazy-fetch only when a row expands. */
  enabled?: boolean
}

/**
 * Current safety verdict for a video. Returns the ``never_checked`` placeholder
 * when no row exists, so callers can render a rescan CTA without 404 handling.
 */
export function useSafetyVerdict({ videoId, lang, enabled = true }: SafetyDetailParams) {
  return useQuery({
    queryKey: queryKeys.safety.verdict(videoId, lang),
    queryFn: async (): Promise<SafetyVerdictOut> => {
      const { data } = await api.GET('/api/v1/safety/{video_id}', {
        params: { path: { video_id: videoId }, query: { lang } },
      })
      if (!data) throw new Error('safety verdict empty')
      return data.data
    },
    enabled: enabled && Boolean(videoId && lang),
    staleTime: 30_000,
  })
}

/**
 * Paginated audit log of layer invocations. Each row records which layer ran
 * (blocklist/llm_judge), its model, the evidence string, latency, and
 * whether it blocked. Limit is hardcoded to 20 — enough for any single video's
 * full chain plus retries — to keep the expanded-row payload small.
 */
export function useSafetyAudit({ videoId, lang, enabled = true }: SafetyDetailParams) {
  return useQuery({
    queryKey: queryKeys.safety.audit(videoId, lang),
    queryFn: async (): Promise<SafetyAuditEntryOut[]> => {
      const { data } = await api.GET('/api/v1/safety/{video_id}/audit', {
        params: { path: { video_id: videoId }, query: { lang, limit: 20 } },
      })
      if (!data) throw new Error('safety audit empty')
      return data.data
    },
    enabled: enabled && Boolean(videoId && lang),
    staleTime: 30_000,
  })
}

export interface RescanInput {
  videoId: string
  lang: string
}

/**
 * Kicks off a background safety rescan. Returns the ``job_id`` — the global
 * jobs feed picks it up and the JobsWatcher fires a toast when it terminates.
 *
 * On success we invalidate the video detail so the UI re-reads the
 * ``safety.status`` field and the "Publicar" path stops complaining.
 */
export function useRescanSafety() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: RescanInput): Promise<SafetyRescanOut> => {
      const { data } = await api.POST('/api/v1/safety/{video_id}/rescan', {
        params: {
          path: { video_id: input.videoId },
          query: { lang: input.lang },
        },
      })
      if (!data) throw new Error('safety rescan empty')
      return data.data
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: queryKeys.jobs.all })
      void qc.invalidateQueries({ queryKey: queryKeys.videos.byId(vars.videoId, vars.lang) })
    },
  })
}

export interface SafetyReviewInput {
  videoId: string
  lang: string
  decision: SafetyReviewDecision
  /** Optional rationale; persisted in ``details.review`` for audit. */
  reason?: string
}

/**
 * Manual approve/reject for a video's safety verdict. Approving flips
 * the row to PASSED so the upload gate stops blocking; rejecting flips
 * it to BLOCKED. Backend also feeds rejected cases into the safety
 * golden set for regression testing.
 *
 * On success we invalidate the verdict + video detail caches so the
 * Project page chip and the upload-blocked banner re-read instantly.
 */
export function useSafetyReview() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: SafetyReviewInput): Promise<SafetyReviewOut> => {
      const { data } = await api.POST('/api/v1/safety/{video_id}/review', {
        params: {
          path: { video_id: input.videoId },
          query: { lang: input.lang },
        },
        body: { decision: input.decision, reason: input.reason ?? '' },
      })
      if (!data) throw new Error('safety review empty')
      return data.data
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: queryKeys.safety.verdict(vars.videoId, vars.lang) })
      void qc.invalidateQueries({ queryKey: queryKeys.safety.audit(vars.videoId, vars.lang) })
      void qc.invalidateQueries({ queryKey: queryKeys.videos.byId(vars.videoId, vars.lang) })
    },
  })
}
